import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BarChart2, MessageCircle, Volume2, Clock, Users, AlertCircle, Zap, TrendingUp
} from '../../components/icons';
import { useAuth } from '../../contexts/AuthContext';
import { isUserCoach } from '../../lib/auth';
import { supabase } from '../../lib/supabase';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { fetchUsageSummary, type UsageSummary } from '../../lib/ai-coach';

type SortKey = 'tokens' | 'voice' | 'messages' | 'name';

function UsageBar({ used, limit, color }: { used: number; limit: number; color: string }) {
  if (limit === 0) return <span className="text-xs text-chocolate-400">Unlimited</span>;
  const pct = Math.min(100, Math.round((used / limit) * 100));
  const barColor = pct >= 100 ? 'bg-coral-500' : pct >= 80 ? 'bg-amber-400' : color;
  return (
    <div className="w-full">
      <div className="flex justify-between text-xs mb-1">
        <span className={pct >= 100 ? 'text-coral-600 font-semibold' : 'text-chocolate-600'}>
          {used.toLocaleString()} / {limit.toLocaleString()}
        </span>
        <span className={pct >= 100 ? 'text-coral-600 font-semibold' : pct >= 80 ? 'text-amber-600' : 'text-chocolate-400'}>
          {pct}%
        </span>
      </div>
      <div className="w-full bg-cream-200 rounded-full h-1.5">
        <div className={`h-1.5 rounded-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function AICoachUsage() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [usageData, setUsageData] = useState<UsageSummary[]>([]);
  const [sortKey, setSortKey] = useState<SortKey>('tokens');
  const [totals, setTotals] = useState({ chatMessages: 0, voiceMinutes: 0, tokens: 0, activeRespondents: 0 });

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }

    const init = async () => {
      try {
        const coach = await isUserCoach(user.id);
        if (!coach) { navigate('/login'); return; }

        const { data: coachRecord } = await supabase
          .from('coaches')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!coachRecord) { navigate('/login'); return; }

        const summary = await fetchUsageSummary(coachRecord.id);
        setUsageData(summary);

        setTotals({
          chatMessages: summary.reduce((s, r) => s + r.totalChatMessages, 0),
          voiceMinutes: summary.reduce((s, r) => s + r.totalVoiceMinutes, 0),
          tokens: summary.reduce((s, r) => s + r.totalTokens, 0),
          activeRespondents: summary.filter(r => r.totalChatMessages > 0 || r.totalVoiceMinutes > 0).length,
        });
      } catch (err) {
        console.error('Error loading usage data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [user, authLoading, navigate]);

  const sorted = [...usageData].sort((a, b) => {
    if (sortKey === 'tokens') return b.totalTokens - a.totalTokens;
    if (sortKey === 'voice') return b.totalVoiceMinutes - a.totalVoiceMinutes;
    if (sortKey === 'messages') return b.totalChatMessages - a.totalChatMessages;
    return a.respondentName.localeCompare(b.respondentName);
  });

  if (authLoading || isLoading) {
    return <LoadingSpinner message="Loading usage data..." />;
  }

  const statCards = [
    { label: 'Tokens This Month', value: totals.tokens.toLocaleString(), icon: Zap, color: 'bg-teal-500' },
    { label: 'Chat Messages', value: totals.chatMessages, icon: MessageCircle, color: 'bg-sage-500' },
    { label: 'Voice Minutes', value: totals.voiceMinutes, icon: Volume2, color: 'bg-warm-500' },
    { label: 'Active Clients', value: totals.activeRespondents, icon: Users, color: 'bg-blue-500' },
  ];

  const SortBtn = ({ k, label }: { k: SortKey; label: string }) => (
    <button
      onClick={() => setSortKey(k)}
      className={`px-3 py-1 text-xs rounded-full transition-colors ${
        sortKey === k
          ? 'bg-sage-500 text-white'
          : 'bg-cream-200 text-chocolate-600 hover:bg-cream-300'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-teal-500 rounded-xl shadow-soft">
              <BarChart2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="freud-heading-xl text-chocolate-900">AI Coach Usage</h1>
              <p className="freud-text-sm text-chocolate-500">Token and voice usage this month across your clients</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {statCards.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.07 }}
              className="freud-card-elevated p-5"
            >
              <div className={`p-2.5 ${stat.color} rounded-xl shadow-soft w-fit mb-3`}>
                <stat.icon className="w-5 h-5 text-white" />
              </div>
              <div className="text-2xl font-semibold text-chocolate-900">{stat.value}</div>
              <div className="text-xs text-chocolate-500 mt-0.5">{stat.label}</div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.32 }}
          className="freud-card-elevated"
        >
          <div className="p-5 border-b border-cream-200 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-teal-500" />
              <h2 className="freud-heading-base text-chocolate-900">Usage by Client</h2>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-chocolate-500">Sort by:</span>
              <SortBtn k="tokens" label="Tokens" />
              <SortBtn k="voice" label="Voice" />
              <SortBtn k="messages" label="Messages" />
              <SortBtn k="name" label="Name" />
            </div>
          </div>

          {sorted.length === 0 ? (
            <div className="text-center py-12 text-chocolate-400">
              <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No usage data yet. Enable AI Coach for your clients to get started.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-cream-100">
                  <tr>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Client</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700 min-w-[180px]">Tokens This Month</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700 min-w-[160px]">Voice Minutes</th>
                    <th className="text-center px-6 py-3 text-sm font-semibold text-chocolate-700">Chat Messages</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Last Active</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-cream-200">
                  {sorted.map((row) => {
                    const tokenPct = row.maxTokensPerMonth > 0 ? row.totalTokens / row.maxTokensPerMonth : 0;
                    const voicePct = row.maxVoiceMinutes > 0 ? row.totalVoiceMinutes / row.maxVoiceMinutes : 0;
                    const isTokenWarn = tokenPct >= 0.8;
                    const isVoiceWarn = voicePct >= 0.8;
                    return (
                      <tr
                        key={row.respondentId}
                        className={`hover:bg-cream-50 ${tokenPct >= 1 || voicePct >= 1 ? 'bg-coral-50/40' : ''}`}
                      >
                        <td className="px-6 py-4">
                          <div className="font-medium text-chocolate-900">{row.respondentName}</div>
                          <div className="text-xs text-chocolate-400">{row.respondentEmail}</div>
                        </td>
                        <td className="px-6 py-4">
                          {row.maxTokensPerMonth === 0 ? (
                            <div className="flex items-center gap-1.5">
                              <Zap className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" />
                              <span className="text-sm font-medium text-chocolate-800">{row.totalTokens.toLocaleString()}</span>
                              <span className="text-xs text-chocolate-400">/ unlimited</span>
                            </div>
                          ) : (
                            <div className="flex items-start gap-2">
                              <Zap className={`w-3.5 h-3.5 flex-shrink-0 mt-1 ${isTokenWarn ? 'text-amber-500' : 'text-teal-500'}`} />
                              <UsageBar used={row.totalTokens} limit={row.maxTokensPerMonth} color="bg-teal-500" />
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {row.maxVoiceMinutes === 0 ? (
                            <div className="flex items-center gap-1.5">
                              <Volume2 className="w-3.5 h-3.5 text-warm-500 flex-shrink-0" />
                              <span className="text-sm font-medium text-chocolate-800">{row.totalVoiceMinutes}</span>
                              <span className="text-xs text-chocolate-400">/ unlimited</span>
                            </div>
                          ) : (
                            <div className="flex items-start gap-2">
                              <Volume2 className={`w-3.5 h-3.5 flex-shrink-0 mt-1 ${isVoiceWarn ? 'text-amber-500' : 'text-warm-500'}`} />
                              <UsageBar used={row.totalVoiceMinutes} limit={row.maxVoiceMinutes} color="bg-warm-500" />
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="inline-flex items-center gap-1.5">
                            <MessageCircle className="w-3.5 h-3.5 text-sage-500" />
                            <span className="text-sm font-medium text-chocolate-800">{row.totalChatMessages}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {row.lastActivity ? (
                            <div className="flex items-center gap-1.5 text-chocolate-600">
                              <Clock className="w-3.5 h-3.5" />
                              <span className="text-sm">{new Date(row.lastActivity).toLocaleDateString()}</span>
                            </div>
                          ) : (
                            <span className="text-xs text-chocolate-300">No activity</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
}
